import { useState, useEffect, useMemo } from 'react';
import { WEBGL_DEMOS } from '@/config/demos.config';
import { getAllPosts } from '@/lib/posts';
import { getAllDayMetadata } from '@/lib/days';
import type { WebGLDemoMetadata } from '@/types/webgl';
import type { PostData } from '@/lib/posts';
import type { DayMetadata } from '@/types';

interface SearchableItem {
  id: string;
  title: string;
  description: string;
  type: 'demo' | 'blog' | 'day' | 'page';
  url: string;
  tags?: string[];
  date?: string;
}

interface UseSearchProps {
  debounceMs?: number;
}

export function useSearch({ debounceMs = 300 }: UseSearchProps = {}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<SearchableItem[]>([]);

  // デバウンス処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [query, debounceMs]);

  // 検索可能なアイテムを準備
  const searchableItems = useMemo<SearchableItem[]>(() => {
    // デモアイテム
    const demoItems: SearchableItem[] = WEBGL_DEMOS
      .filter((demo): demo is WebGLDemoMetadata & { implemented: true } => 
        demo.implemented === true
      )
      .map(demo => ({
        id: demo.id,
        title: demo.name,
        description: demo.description,
        type: 'demo',
        url: `/demos/${demo.slug}`,
        tags: [...(demo.tags || []), ...(demo.technologies || [])],
      }));

    // ブログアイテム
    let blogItems: SearchableItem[] = [];
    try {
      const posts = getAllPosts();
      blogItems = posts.map((post: PostData) => ({
        id: post.slug,
        title: post.title,
        description: post.description || post.summary || '',
        type: 'blog',
        url: `/blog/${post.slug}`,
        tags: post.tags,
        date: post.date,
      }));
    } catch (error) {
      console.warn('Failed to load blog posts for search:', error);
    }

    // チャレンジアイテム
    const dayItems: SearchableItem[] = getAllDayMetadata().map((day: DayMetadata) => ({
      id: day.id.toString(),
      title: day.title,
      description: day.description,
      type: 'day',
      url: `/day/${day.id}`,
      tags: [...day.snsHashtags, ...day.technologies],
      date: day.publishedAt,
    }));

    // 固定ページアイテム
    const pageItems: SearchableItem[] = [
      { id: 'home', title: 'ホーム', description: 'メインページ', type: 'page', url: '/' },
      { id: 'demos', title: 'デモ', description: 'WebGLデモ一覧', type: 'page', url: '/demos' },
      { id: 'gallery', title: 'ギャラリー', description: '作品ギャラリー', type: 'page', url: '/gallery' },
      { id: 'blog', title: 'ブログ', description: '技術ブログ', type: 'page', url: '/blog' },
      { id: 'playground', title: 'プレイグラウンド', description: 'コードプレイグラウンド', type: 'page', url: '/playground' },
      { id: 'ai-assistant', title: 'AIアシスタント', description: 'AIコードアシスタント', type: 'page', url: '/ai-assistant' },
      { id: 'community', title: 'コミュニティ', description: 'コミュニティギャラリー', type: 'page', url: '/community' },
      { id: 'learn', title: '学習', description: '学習リソース', type: 'page', url: '/learn' },
      { id: 'about', title: 'について', description: 'プロジェクトについて', type: 'page', url: '/about' },
    ];

    return [...demoItems, ...blogItems, ...dayItems, ...pageItems];
  }, []);

  // 検索処理
  useEffect(() => {
    if (!debouncedQuery) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // シミュレートされた検索処理（実際のアプリケーションではAPI呼び出しなど）
    const search = async () => {
      try {
        // 小文字で検索
        const queryLower = debouncedQuery.toLowerCase();
        
        // 検索結果をフィルタリング
        const filteredResults = searchableItems.filter(item => {
          // タイトル、説明、タグで検索
          return (
            item.title.toLowerCase().includes(queryLower) ||
            item.description.toLowerCase().includes(queryLower) ||
            (item.tags && item.tags.some(tag => tag.toLowerCase().includes(queryLower)))
          );
        });

        // 結果をスコアでソート（簡略化された実装）
        const sortedResults = [...filteredResults].sort((a, b) => {
          // 完全一致を優先
          const aTitleMatch = a.title.toLowerCase() === queryLower;
          const bTitleMatch = b.title.toLowerCase() === queryLower;
          
          if (aTitleMatch && !bTitleMatch) return -1;
          if (!aTitleMatch && bTitleMatch) return 1;
          
          // 部分一致の位置でソート
          const aTitleIndex = a.title.toLowerCase().indexOf(queryLower);
          const bTitleIndex = b.title.toLowerCase().indexOf(queryLower);
          
          if (aTitleIndex !== bTitleIndex) {
            return aTitleIndex - bTitleIndex;
          }
          
          // タイプでソート（デモ、ブログ、チャレンジ、ページの順）
          const typeOrder = { demo: 1, blog: 2, day: 3, page: 4 };
          return (typeOrder[a.type] || 5) - (typeOrder[b.type] || 5);
        });

        // 最大10件に制限
        setResults(sortedResults.slice(0, 10));
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedQuery, searchableItems]);

  return {
    query,
    setQuery,
    results,
    isLoading,
  };
}
